import React, { useMemo } from 'react';
import { CatalogProvider } from '@context/catalog.context';
import clsx from 'clsx';
import useCatalogSchema from '@hooks/catalog-product/use-catalog-schema';
import CatalogSchema from '@components/structured-schemas/catalog-schema';
import ProductSection from '@components/catalog/product-section';
import BottomPaginationSection from '@components/catalog/bottom-pagination-section';
import { CampaignProvider, CampaignProviderProps } from '@context/campaign.context';
import CatalogDatalayer from '@components/catalog/catalog-datalayer/catalog-datalayer';
import ManageRecentSearches from '@components/catalog/manage-recent-searches/manage-recent-searches';
import { PLPInnerFeedContextProvider } from '@context/plp-inner-feed.context';
import { CatalogProviderProps } from '@interfaces/models/catalogContextProps';
import CatalogTitleBlock from '@components/catalog/catalog-title-block/catalog-title-block';
import CatalogHotfilters from '@components/catalog/catalog-hotfilters/catalog-hotfilters';
import CatalogSaveSearchesRow from '@components/catalog/catalog-hotfilters/catalog-save-searches-row/catalog-save-searches-row';
import dynamic from 'next/dynamic';
import styles from './product-search.module.scss';

export type ProductSearchProps = {
  catalogContextProps: Omit<CatalogProviderProps, 'children'>;
  campaignContextProps?: Omit<CampaignProviderProps, 'children'>;
  // SEO links, SEO footer
  footerContent?: React.ReactNode;
};

const CatalogGuidedSearch = dynamic(() => import('@components/catalog/catalog-guided-search/catalog-guided-search'));

const ProductSearch: React.FC<ProductSearchProps> = (props): React.JSX.Element => {
  const { catalogContextProps, campaignContextProps, footerContent } = props;
  const schemaCatalogPage = useCatalogSchema();

  const scrollRef = React.useRef<HTMLDivElement>(null);

  const bottomSection = useMemo(
    () => (
      <>
        <BottomPaginationSection />
        {footerContent && <>{footerContent}</>}
      </>
    ),
    [footerContent],
  );

  return (
    <>
      <CampaignProvider {...campaignContextProps}>
        <CatalogProvider {...{ ...catalogContextProps, scrollRef }}>
          <PLPInnerFeedContextProvider>
            <ManageRecentSearches />
            <CatalogDatalayer>
              {schemaCatalogPage?.isSingleSchema && <CatalogSchema />}
              <main className={styles.catalogPage}>
                <div className={styles.catalogPage__catalog}>
                  <div className="innerContainer">
                    <div className={clsx(styles.catalog)}>
                      <div className={styles.catalog__columnProductList}>
                        <CatalogTitleBlock metadata={catalogContextProps.metadata} />
                        {catalogContextProps.isGuidedSearchEnabled && <CatalogGuidedSearch />}
                        <CatalogHotfilters scrollRef={scrollRef} />
                        <CatalogSaveSearchesRow />
                        <ProductSection />
                      </div>
                    </div>
                    <div className={styles.bottomSectionWrapper}>{bottomSection}</div>
                  </div>
                </div>
              </main>
            </CatalogDatalayer>
          </PLPInnerFeedContextProvider>
        </CatalogProvider>
      </CampaignProvider>
    </>
  );
};

export default ProductSearch;
